import { FormSpy } from 'react-final-form';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { KnmAttachmentsSigning } from 'KnmPackage/MainModule/components/KnmAttachmentsSigning';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDocumentTypeSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCatalogErknmKnmDocumentTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IconInfo } from 'UIPackage/IconsModule/components/IconInfo';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';

import styles from './ErknmReasonsAttachment.module.scss';
import { ErknmReasonsDocumentTypesEnum } from '../enums/ErknmReasonsDocumentTypesEnum';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { ErknmEvidence } from './ErknmEvidence/ErknmEvidence';

interface IErknmReasonsAttachmentProps {
  formRef: IKnmFormRef | undefined;
  id: string;
  index: number;
  makeRemoveReasonDocumentHandler: (i: number) => () => unknown;
  reasonDocumentFieldName: string;
  watchOnly: boolean;
  type?: IKnmDocumentTypeSelectOption;
}

interface IErknmReasonsAttachmentStoreProps {
  erknmKnmDocumentTypesSelectOptions: IKnmDocumentTypeSelectOption[];
  settings: IAdminSetting[];
}

const ErknmReasonsAttachmentWithoutStore = ({
  formRef,
  id,
  index,
  makeRemoveReasonDocumentHandler,
  reasonDocumentFieldName,
  watchOnly,
  erknmKnmDocumentTypesSelectOptions,
  type,
  settings,
}: IErknmReasonsAttachmentProps & IErknmReasonsAttachmentStoreProps) => {
  const filteredErknmKnmDocumentTypesSelectOptions = useMemo(
    () => erknmKnmDocumentTypesSelectOptions.filter(item => item.code in ErknmReasonsDocumentTypesEnum),
    [erknmKnmDocumentTypesSelectOptions],
  );

  const created = formRef?.current.getFieldState('created')?.value;

  const isUseEvidence = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_EVIDENCE_4_1_25_STARTS_FROM,
  );

  const [isVisibleSignModal, setIsVisibleSignModal] = useState<boolean>(false);

  const handleOpenSignModal = useCallback(() => {
    setIsVisibleSignModal(true);
  }, []);

  const handleCloseSignModal = useCallback(() => {
    setIsVisibleSignModal(false);
  }, []);

  return (
    <Row key={id} margin="medium" isListItem>
      <div className="shared-deleting-row">
        <FieldBlock
          label={
            <>
              Тип документа
              <ErknmFieldMark fieldName={`${reasonDocumentFieldName}.type`} />
            </>
          }
          color="black"
        >
          <SelectInputField
            className="shared-field_margin-bottom"
            id={`${reasonDocumentFieldName}.type`}
            name={`${reasonDocumentFieldName}.type`}
            options={filteredErknmKnmDocumentTypesSelectOptions}
            isClearable={false}
            disabled={watchOnly}
            validate={makeRequired('Поле не может быть пустым')}
          />
        </FieldBlock>
        {isUseEvidence && type?.code === ErknmReasonsDocumentTypesEnum.VP_VIII ? (
          <ErknmEvidence
            id={`${id}.violationEvidences`}
            name={`${reasonDocumentFieldName}.violationEvidences`}
            watchOnly={watchOnly}
            formRef={formRef}
            isDecision={false}
            isKnm={true}
            isPm={false}
            maxDate={new Date()}
          />
        ) : (
          <>
            <FieldBlock id={`${id}.attachments`}>
              <span>
                Файлы
                <ErknmFieldMark fieldName={`${reasonDocumentFieldName}.attachments`} />
                <Tooltip
                  overlayClassName={styles.FilesTooltip}
                  placement="top"
                  content={
                    <div className={styles.FilesTooltipContent}>
                      Приложенные документы-основания проведения КНМ не отображаются в выписке и недоступны
                      контролируемому лицу и его представителю
                    </div>
                  }
                >
                  <span>
                    <IconInfo className={styles.FilesTooltipIcon} />
                  </span>
                </Tooltip>
              </span>
              <Button
                className={styles.AddFileButton}
                text="Добавить"
                type="button"
                variant="link"
                color="primary"
                onClick={handleOpenSignModal}
                disabled={watchOnly}
              />
            </FieldBlock>
            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                const fieldError = props.errors?.reasonDocuments && props.errors?.reasonDocuments[index]?.attachments;
                const isFieldTouched = props.touched?.places;

                if (isFieldTouched && fieldError) {
                  return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
                }

                return null;
              }}
            </FormSpy>
            <KnmAttachmentsSigning
              isWatchOnly={watchOnly}
              name={`${reasonDocumentFieldName}.attachments`}
              isVisibleSignModal={isVisibleSignModal}
              closeSignModal={handleCloseSignModal}
              formRef={formRef}
            />
          </>
        )}
      </div>
      <CloseButton
        className={cn('shared-deleting-row-btn', styles.DocumentDeleteButton)}
        disabled={watchOnly}
        onClick={makeRemoveReasonDocumentHandler(index)}
      />
    </Row>
  );
};

const mapStateToProps = (state: IRootState) => ({
  erknmKnmDocumentTypesSelectOptions: getCatalogErknmKnmDocumentTypesSelectOptions(state),
  settings: getAdminFetchSettings(state),
});

export const ErknmReasonsAttachment = connect(mapStateToProps)(ErknmReasonsAttachmentWithoutStore);
